import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { EmojiEvents, Star, CardGiftcard, Casino } from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';

interface Operator {
  id: number;
  name: string;
  code: string;
}

interface ResultsEntryForm {
  operatorId: number | '';
  drawDate: Date | null;
  gameType: string;
  firstPrize: string;
  secondPrize: string;
  thirdPrize: string;
  specialPrizes: string[];
  consolationPrizes: string[];
  fiveDFirstPrize: string;
  fiveDSecondPrize: string;
  fiveDThirdPrize: string;
  sixDPrize: string;
}

const ResultsEntry: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(null);

  const [formData, setFormData] = useState<ResultsEntryForm>({
    operatorId: '',
    drawDate: new Date(),
    gameType: '4D',
    firstPrize: '',
    secondPrize: '',
    thirdPrize: '',
    specialPrizes: Array(10).fill(''),
    consolationPrizes: Array(10).fill(''),
    fiveDFirstPrize: '',
    fiveDSecondPrize: '',
    fiveDThirdPrize: '',
    sixDPrize: ''
  });

  // Fetch operators on component mount
  useEffect(() => {
    const fetchOperators = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/lottery-results/operators');
        setOperators(response.data);
      } catch (err) {
        setError('Failed to load operators');
        console.error('Error fetching operators:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOperators();
  }, []);

  // Update selected operator when operatorId changes
  useEffect(() => {
    const operator = operators.find(op => op.id === formData.operatorId);
    setSelectedOperator(operator || null);
  }, [formData.operatorId, operators]);

  // Helper function to check if operator supports 5D
  const supports5D = (): boolean => {
    if (!selectedOperator) return false;
    const code = selectedOperator.code.toUpperCase();
    // 5D only for Sports Toto (TOTO)
    return code === 'TOTO';
  };

  // Helper function to check if operator supports 6D
  const supports6D = (): boolean => {
    if (!selectedOperator) return false;
    const code = selectedOperator.code.toUpperCase();
    // 6D for Sports Toto (TOTO), 9 Lotto (PERDANA), Grand Dragon (GRAND_DRAGON)
    return code === 'TOTO' || code === 'PERDANA' || code === 'GRAND_DRAGON';
  };

  const handleInputChange = (field: keyof ResultsEntryForm, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (success || error) {
      setSuccess(null);
      setError(null);
    }
  };

  const handleSpecialPrizeChange = (index: number, value: string) => {
    const newSpecialPrizes = [...formData.specialPrizes];
    newSpecialPrizes[index] = value;
    handleInputChange('specialPrizes', newSpecialPrizes);
  };

  const handleConsolationPrizeChange = (index: number, value: string) => {
    const newConsolationPrizes = [...formData.consolationPrizes];
    newConsolationPrizes[index] = value;
    handleInputChange('consolationPrizes', newConsolationPrizes);
  };

  // Generate random number with specified digits
  const generateRandomNumber = (digits: number): string => {
    const min = 0;
    const max = Math.pow(10, digits) - 1;
    const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
    return randomNum.toString().padStart(digits, '0');
  };

  // Fill all fields with random numbers
  const handleGenerateRandomNumbers = () => {
    // Generate 4D numbers (0000-9999)
    const random4D = () => generateRandomNumber(4);
    
    setFormData(prev => ({
      ...prev,
      firstPrize: random4D(),
      secondPrize: random4D(),
      thirdPrize: random4D(),
      specialPrizes: Array(10).fill('').map(() => random4D()),
      consolationPrizes: Array(10).fill('').map(() => random4D()),
      fiveDFirstPrize: supports5D() ? generateRandomNumber(5) : '',
      fiveDSecondPrize: supports5D() ? generateRandomNumber(5) : '',
      fiveDThirdPrize: supports5D() ? generateRandomNumber(5) : '',
      sixDPrize: supports6D() ? generateRandomNumber(6) : ''
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.operatorId) return 'Please select an operator';
    if (!formData.drawDate) return 'Please select a draw date';
    if (!formData.firstPrize.trim()) return 'Please enter 1st Prize';
    if (!formData.secondPrize.trim()) return 'Please enter 2nd Prize';
    if (!formData.thirdPrize.trim()) return 'Please enter 3rd Prize';
    
    // Check if at least one special prize is entered
    const hasSpecialPrizes = formData.specialPrizes.some(prize => prize.trim() !== '');
    if (!hasSpecialPrizes) return 'Please enter at least one Special Prize';
    
    // Check if at least one consolation prize is entered
    const hasConsolationPrizes = formData.consolationPrizes.some(prize => prize.trim() !== '');
    if (!hasConsolationPrizes) return 'Please enter at least one Consolation Prize';

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Filter out empty prizes
      const specialPrizes = formData.specialPrizes.filter(prize => prize.trim() !== '');
      const consolationPrizes = formData.consolationPrizes.filter(prize => prize.trim() !== '');

      // Format date as YYYY-MM-DD in local timezone
      let formattedDate = '';
      if (formData.drawDate) {
        const year = formData.drawDate.getFullYear();
        const month = String(formData.drawDate.getMonth() + 1).padStart(2, '0');
        const day = String(formData.drawDate.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }

      const payload: any = {
        operatorId: formData.operatorId,
        drawDate: formattedDate,
        gameType: formData.gameType,
        firstPrize: formData.firstPrize.trim(),
        secondPrize: formData.secondPrize.trim(),
        thirdPrize: formData.thirdPrize.trim(),
        specialPrizes,
        consolationPrizes
      };

      // Add 5D results if operator supports it and data is provided
      if (supports5D()) {
        if (formData.fiveDFirstPrize.trim()) payload.fiveDFirstPrize = formData.fiveDFirstPrize.trim();
        if (formData.fiveDSecondPrize.trim()) payload.fiveDSecondPrize = formData.fiveDSecondPrize.trim();
        if (formData.fiveDThirdPrize.trim()) payload.fiveDThirdPrize = formData.fiveDThirdPrize.trim();
      }

      // Add 6D result if operator supports it and data is provided
      if (supports6D()) {
        if (formData.sixDPrize.trim()) payload.sixDPrize = formData.sixDPrize.trim();
      }

      await axios.post('/lottery-results/manual', payload);
      
      setSuccess(`Results successfully inserted for ${selectedOperator?.name} on ${format(formData.drawDate!, 'yyyy-MM-dd')}`);
      
      // Reset form
      setFormData({
        operatorId: '',
        drawDate: new Date(),
        gameType: '4D',
        firstPrize: '',
        secondPrize: '',
        thirdPrize: '',
        specialPrizes: Array(10).fill(''),
        consolationPrizes: Array(10).fill(''),
        fiveDFirstPrize: '',
        fiveDSecondPrize: '',
        fiveDThirdPrize: '',
        sixDPrize: ''
      });
      setSelectedOperator(null);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to insert results';
      setError(errorMessage);
      console.error('Error inserting results:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading operators...
        </Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Manual Result Entry
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Enter 4D lottery draw results manually
            </Typography>
            {formData.operatorId && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Casino />}
                onClick={handleGenerateRandomNumbers}
                sx={{ mt: 2 }}
              >
                Generate Random Numbers
              </Button>
            )}
          </Box>

          {/* Success/Error Messages */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Operator and Date Selection */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={formData.operatorId}
                    label="Operator"
                    onChange={(e) => handleInputChange('operatorId', e.target.value)}
                  >
                    {operators.map((operator) => (
                      <MenuItem key={operator.id} value={operator.id}>
                        {operator.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Draw Date"
                  value={formData.drawDate}
                  onChange={(date) => handleInputChange('drawDate', date)}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
            </Grid>

            {/* Operator Header Display */}
            {selectedOperator && formData.drawDate && (
              <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #ff6b9d 0%, #c44569 100%)' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                    {selectedOperator.name}
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'white' }}>
                    {format(formData.drawDate, 'yyyy-MM-dd [EEE]')}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Main Prizes */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEvents sx={{ mr: 1, color: 'gold' }} />
                <Typography variant="h6">Main Prizes</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="1ST PRIZE"
                    value={formData.firstPrize}
                    onChange={(e) => handleInputChange('firstPrize', e.target.value)}
                    required
                    inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffd700',
                        '& fieldset': { borderColor: '#ffb300' },
                        '&:hover fieldset': { borderColor: '#ff8f00' }
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="2ND PRIZE"
                    value={formData.secondPrize}
                    onChange={(e) => handleInputChange('secondPrize', e.target.value)}
                    required
                    inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ff6b9d',
                        color: 'white',
                        '& fieldset': { borderColor: '#c44569' },
                        '&:hover fieldset': { borderColor: '#a55eea' },
                        '& input': { color: 'white' },
                        '& label': { color: 'white' }
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="3RD PRIZE"
                    value={formData.thirdPrize}
                    onChange={(e) => handleInputChange('thirdPrize', e.target.value)}
                    required
                    inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#26de81',
                        color: 'white',
                        '& fieldset': { borderColor: '#20bf6b' },
                        '&:hover fieldset': { borderColor: '#0fb9b1' },
                        '& input': { color: 'white' },
                        '& label': { color: 'white' }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Special Prizes */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Star sx={{ mr: 1, color: 'gold' }} />
                <Typography variant="h6">Special (10 numbers)</Typography>
              </Box>
              
              <Grid container spacing={1}>
                {formData.specialPrizes.map((prize, index) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={index}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Special ${index + 1}`}
                      value={prize}
                      onChange={(e) => handleSpecialPrizeChange(index, e.target.value)}
                      inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff9c4',
                          '& fieldset': { borderColor: '#ffd700' },
                          '&:hover fieldset': { borderColor: '#ffb300' }
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Consolation Prizes */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CardGiftcard sx={{ mr: 1, color: '#a55eea' }} />
                <Typography variant="h6">Consolation (10 numbers)</Typography>
              </Box>
              
              <Grid container spacing={1}>
                {formData.consolationPrizes.map((prize, index) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={index}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Consolation ${index + 1}`}
                      value={prize}
                      onChange={(e) => handleConsolationPrizeChange(index, e.target.value)}
                      inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f3e5f5',
                          '& fieldset': { borderColor: '#a55eea' },
                          '&:hover fieldset': { borderColor: '#8e44ad' }
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* 5D Results - Only for Sports Toto */}
            {supports5D() && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmojiEvents sx={{ mr: 1, color: '#00bcd4' }} />
                  <Typography variant="h6">5D Results (Sports Toto only)</Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="5D 1ST PRIZE"
                      value={formData.fiveDFirstPrize}
                      onChange={(e) => handleInputChange('fiveDFirstPrize', e.target.value)}
                      inputProps={{ maxLength: 5, pattern: '[0-9]*' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#e0f7fa',
                          '& fieldset': { borderColor: '#00bcd4' },
                          '&:hover fieldset': { borderColor: '#0097a7' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="5D 2ND PRIZE"
                      value={formData.fiveDSecondPrize}
                      onChange={(e) => handleInputChange('fiveDSecondPrize', e.target.value)}
                      inputProps={{ maxLength: 5, pattern: '[0-9]*' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#e0f7fa',
                          '& fieldset': { borderColor: '#00bcd4' },
                          '&:hover fieldset': { borderColor: '#0097a7' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="5D 3RD PRIZE"
                      value={formData.fiveDThirdPrize}
                      onChange={(e) => handleInputChange('fiveDThirdPrize', e.target.value)}
                      inputProps={{ maxLength: 5, pattern: '[0-9]*' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#e0f7fa',
                          '& fieldset': { borderColor: '#00bcd4' },
                          '&:hover fieldset': { borderColor: '#0097a7' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* 6D Results - Only for Sports Toto, 9 Lotto, Grand Dragon */}
            {supports6D() && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmojiEvents sx={{ mr: 1, color: '#9c27b0' }} />
                  <Typography variant="h6">6D Result (Sports Toto / 9 Lotto / Grand Dragon)</Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="6D PRIZE"
                      value={formData.sixDPrize}
                      onChange={(e) => handleInputChange('sixDPrize', e.target.value)}
                      inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f3e5f5',
                          '& fieldset': { borderColor: '#9c27b0' },
                          '&:hover fieldset': { borderColor: '#7b1fa2' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Submit Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                  }
                }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Inserting...
                  </>
                ) : (
                  'Insert Results'
                )}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default ResultsEntry;
